import { useState, useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateProfile } from '../services/profile';
import { validateImageFile, compressImageIfNeeded } from '../utils/imageUtils';

/**
 * Custom hook for profile image upload with validation and compression (web only)
 * @param {Object} options
 * @param {string} options.fieldName - API field name ('profile_image' or 'clinic_profile_image')
 * @param {string} options.scope - API scope ('personal' or 'clinic_owner')
 * @param {string} [options.role] - Target model on the API ('clinic' to write to the clinic
 *   instead of the user/role record). Required for 'clinic_profile_image', otherwise the
 *   backend drops the field and returns success without saving.
 * @param {number} options.maxSizeMB - Max file size in MB (default: 5)
 * @param {Function} options.onSuccess - Success callback
 * @param {Function} options.onError - Error callback
 * @returns {{ isUploading: boolean, error: string | null, triggerImagePicker: () => void, clearError: () => void }}
 */
export const useProfileImageUpload = ({
  fieldName,
  scope,
  role,
  maxSizeMB = 5,
  onSuccess,
  onError,
}) => {
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();
  const isProcessingRef = useRef(false);

  const uploadMutation = useMutation({
    mutationFn: (base64Image) => {
      return updateProfile({
        scope,
        ...(role ? { role } : {}),
        [fieldName]: base64Image,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['clinics'] });
      // WebNavbar renders its avatar from ['overview'], not ['profile'] — without
      // this the navbar keeps the old picture until a reload.
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      setError(null);
      onSuccess?.();
    },
    onError: (err) => {
      const errorMessage =
        err?.response?.data?.message || 'Failed to upload image';
      setError(errorMessage);
      onError?.(errorMessage);
    },
  });

  const triggerImagePicker = useCallback(() => {
    // Prevent opening picker while upload is in progress
    if (uploadMutation.isPending || isProcessingRef.current) {
      return;
    }

    // Create hidden file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/jpg,image/webp';

    const cleanup = () => {
      input.remove();
      isProcessingRef.current = false;
    };

    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) {
        cleanup();
        return;
      }

      isProcessingRef.current = true;

      // Validate file
      const validation = validateImageFile(file, maxSizeMB);
      if (!validation.valid) {
        setError(validation.error);
        onError?.(validation.error);
        cleanup();
        return;
      }

      try {
        // Compress and convert to base64
        const base64 = await compressImageIfNeeded(file);

        // Upload
        uploadMutation.mutate(base64);
      } catch (err) {
        const errorMessage = 'Failed to process image';
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        cleanup();
      }
    };

    // Trigger file picker
    input.click();
  }, [maxSizeMB, uploadMutation, onError]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isUploading: uploadMutation.isPending,
    error,
    triggerImagePicker,
    clearError,
  };
};
